// プレイヤー別の進捗（純ロジック / レンダリング・I/O 非依存）。
// 原作 `StatsModel`（形状×爆弾率ごとの統計）と `GameScene` のアンロック判定の移植。
//
// ## 二角取りとの形の違い
//
// 二角取りは「1ステージ = 18パート」で**ステージが増えていく**が、
// Triangle Sweeper は **15形状 × 3難易度 = 45 の固定枠**でステージの概念が無い。
// D1 の `player_stage_progress.parts` は JSON テキスト列なので**マイグレーションは要らない**
// （サーバーは中身を解釈しない）。**形の違いはこのファイルで吸収する**。
//
// 1マスの記録そのもの（stars/best/fastestMs/clears/games/scoreSum/playMs）は
// [platform/stageRecord.ts](@/platform/stageRecord) の共通形をそのまま使う＝★が共通通貨になる。
import {
  type StageRecord,
  emptyRecord,
  normalizeRecord,
  MAX_RECORD_STARS,
} from '@/platform/stageRecord';
import { SHAPE_COUNT } from './shapes';
import {
  BOMB_LEVEL_COUNT,
  INITIAL_UNLOCKED_SHAPES,
  unlockThreshold,
  type BombLevel,
} from '../config/gameConfig';

/** 記録の枠の数（15形状 × 3難易度 = 45）。 */
export const SLOT_COUNT = SHAPE_COUNT * BOMB_LEVEL_COUNT;

/** 1形状ぶんで得られる★の上限（3難易度 × ★3 = 9）。 */
export const MAX_STARS_PER_SHAPE = BOMB_LEVEL_COUNT * MAX_RECORD_STARS;

/** `(形状, 難易度)` → 記録の添字。**並びは形状ごとに Easy/Normal/Hard**。 */
export function slotIndex(shapeIndex: number, level: BombLevel): number {
  return shapeIndex * BOMB_LEVEL_COUNT + level;
}

/** 添字 → `(形状, 難易度)`。 */
export function fromSlot(slot: number): { shapeIndex: number; level: BombLevel } {
  return {
    shapeIndex: Math.floor(slot / BOMB_LEVEL_COUNT),
    level: (slot % BOMB_LEVEL_COUNT) as BombLevel,
  };
}

/** 45枠ぶんの空記録。 */
export function emptyProgress(): StageRecord[] {
  return Array.from({ length: SLOT_COUNT }, emptyRecord);
}

/**
 * 保存済み（localStorage / サーバ）の進捗を現行の形へ揃える。
 * 長さが足りなければ空で埋め、多ければ切る（形状を増減させても壊れない）。
 */
export function normalizeProgress(v: unknown): StageRecord[] {
  const arr = Array.isArray(v) ? v : [];
  return Array.from({ length: SLOT_COUNT }, (_, i) =>
    normalizeRecord(arr[i], MAX_RECORD_STARS),
  );
}

// ---------------------------------------------------------------- アンロック
//
// 🔴 **条件は通算★**（2026-08-06 にユーザー決定・`UNLOCK_STARS_PER_SHAPE` の注記が正本）。
// - 形状ごとの★ = 3難易度の自己ベスト★の合計（**最大 9**）
// - 合計は**解放済みの形状ぶんだけ**数える（遊べない形状の★は入りようがない）
// - 合計が閾値（6★ごと）以上になった形状が開く
//
// ⚠️ 経緯: 当初は独自の「アンロックポイント」だったが、**画面に出ていない値**を条件に
//    していたため何をすれば開くのか分からなかった。★は形状選択画面に出ているので、
//    **見えている値がそのまま条件**になる。過去の設計（原作 `5×(n+1)` で詰む →
//    `3×(n-1)` で Easy 専門が詰む → クリア形状数）はいずれもこの点で不親切だった。
//
// 解放状態を**保存せず記録から導出する**のが Web 版の方針。原作は `shapeUnLocked` を
// 保存していたが、導出なら「記録は在るのにロックされたまま」という食い違いが起きない
// （サーバーとローカルで別々に持つ値が1つ減る）。
// ★は自己ベスト（`applyClear` が `Math.max`）なので**解放が後戻りすることはない**。

/**
 * その形状で得ている★（0..9）。3難易度の自己ベスト★の合計。
 * ⚠️ 「全難易度クリア済みか」は**これでは判定できない**（★0クリアがあるため）＝
 * `isShapeFullyCleared` を使うこと。
 */
export function shapeStars(records: readonly StageRecord[], shapeIndex: number): number {
  let stars = 0;
  for (let level = 0; level < BOMB_LEVEL_COUNT; level++) {
    stars += records[slotIndex(shapeIndex, level as BombLevel)].stars;
  }
  return stars;
}

/**
 * 解放済みの形状（`true` の数だけ遊べる）。
 *
 * 最初の `INITIAL_UNLOCKED_SHAPES`(3) は無条件。以降は「解放済み形状の★合計」が
 * 閾値以上になれば開く。**不動点まで回す**のは、記録を復元したときに
 * 「ロック中の形状に記録がある」状態がありうるため（その形状が開いた瞬間に
 * その★も合計へ入るのが自然）。通常のプレイでは1周で収束する。
 */
export function unlockedShapes(records: readonly StageRecord[]): boolean[] {
  const unlocked = Array.from({ length: SHAPE_COUNT }, (_, i) => i < INITIAL_UNLOCKED_SHAPES);
  for (;;) {
    const total = unlocked.reduce((a, on, i) => a + (on ? shapeStars(records, i) : 0), 0);
    let changed = false;
    for (let i = 0; i < SHAPE_COUNT; i++) {
      if (!unlocked[i] && total >= unlockThreshold(i)) {
        unlocked[i] = true;
        changed = true;
      }
    }
    if (!changed) return unlocked;
  }
}

/** 解放済みの形状数。 */
export function unlockedCount(records: readonly StageRecord[]): number {
  return unlockedShapes(records).filter(Boolean).length;
}

/**
 * **解放済みの形状だけを巡回する**（タイトル画面の ◀▶ 送り）。
 *
 * 原作 `TitleScene:moveNextShapeLayer` はロック中も含めて全形状を順に送り、
 * ロックのときは PLAY を隠して `ImageLOCK.png` を出していた。Web 版は**ロック中を飛ばす**
 * ＝押しても遊べない画面を挟まない（遊べない形状は形状一覧で見えるので、存在は隠れない）。
 *
 * 先頭 `INITIAL_UNLOCKED_SHAPES` 個は常に解放済みなので、必ず1つは見つかる。
 * 万一ゼロなら `from` を返す（無限ループにしない）。
 */
export function stepUnlockedShape(
  unlocked: readonly boolean[],
  from: number,
  delta: number,
): number {
  const n = unlocked.length;
  if (n === 0) return 0;
  let i = ((from % n) + n) % n;
  for (let step = 0; step < n; step++) {
    i = (i + delta + n) % n;
    if (unlocked[i]) return i;
  }
  return from;
}

/**
 * 保存された形状番号を**今遊べる形状へ寄せる**。
 * 記録を消した直後など「ロック中の形状を指したまま」の状態を弾くために使う。
 */
export function clampToUnlockedShape(unlocked: readonly boolean[], shapeIndex: number): number {
  const n = unlocked.length;
  if (n === 0) return 0;
  const i = ((shapeIndex % n) + n) % n;
  return unlocked[i] ? i : stepUnlockedShape(unlocked, i, 1);
}

/**
 * 解放に効いている★の合計（**解放済み形状ぶんだけ**）。
 * ⚠️ 画面右上の「★合計」は全45枠を数える `totalStars` のほう。ロック中の形状に
 * 記録が残っている状況（進捗の復元直後）では**両者は一致しない**。
 */
export function totalUnlockStars(records: readonly StageRecord[]): number {
  const unlocked = unlockedShapes(records);
  return unlocked.reduce((a, on, i) => a + (on ? shapeStars(records, i) : 0), 0);
}

/**
 * 次の形状の解放状況。**形状選択画面に「あと ★N」を出すため**にある
 * （原作の弱点は閾値そのものより**進捗が見えないこと**だった＝見えない条件は報酬にならない）。
 * 全部解放済みなら `null`。
 */
export function nextUnlock(
  records: readonly StageRecord[],
): { shapeIndex: number; need: number; have: number; remaining: number } | null {
  const unlocked = unlockedShapes(records);
  const next = unlocked.indexOf(false);
  if (next < 0) return null;
  const have = totalUnlockStars(records);
  const need = unlockThreshold(next);
  return { shapeIndex: next, need, have, remaining: Math.max(0, need - have) };
}

// ---------------------------------------------------------------- 解錠（UnLock）
//
// 2026-08-06 追加。★が足りただけでは遊べるようにせず、**形状選択画面の
// UnLock ボタンを押して初めて開く**（それまでは鍵マークのまま）。
// 自動で開くと「いつ増えたのか気づけない」というのが発端（ユーザー指摘）。
//
// ⚠️ 「押した／押していない」は**記録から導出できない唯一の状態**なので保存する
// （[net/unlock.ts](../net/unlock.ts)）。導出できるもの＝**★が足りているか**は
// 従来どおり `unlockedShapes` が毎回計算する。この2つを混ぜないこと:
//   - `unlockedShapes(records)` … **資格**（★が足りている）
//   - `playableShapes(records, claimed)` … **実際に遊べる**（資格 かつ 解錠済み）
//   - `pendingUnlocks(records, claimed)` … **UnLock ボタンを出す**（資格 かつ 未解錠）
//
// 資格の計算（`unlockedShapes`）に解錠は関わらせない。未解錠の形状は遊べない＝
// クリア記録が付かない＝★も 0 なので、混ぜても結果は同じで式だけ複雑になる。

/**
 * その形状は解錠済みか。
 * **先頭 `INITIAL_UNLOCKED_SHAPES` は無条件**（最初から開いているものに UnLock は要らない）
 * なので、保存する集合には入れない。
 */
export function isClaimed(claimed: ReadonlySet<number>, shapeIndex: number): boolean {
  return shapeIndex < INITIAL_UNLOCKED_SHAPES || claimed.has(shapeIndex);
}

/** 実際に遊べる形状 ＝ ★が足りている **かつ** 解錠済み。 */
export function playableShapes(
  records: readonly StageRecord[],
  claimed: ReadonlySet<number>,
): boolean[] {
  return unlockedShapes(records).map((on, i) => on && isClaimed(claimed, i));
}

/** UnLock ボタンを出す形状 ＝ ★は足りているが未解錠。**昇順**。 */
export function pendingUnlocks(
  records: readonly StageRecord[],
  claimed: ReadonlySet<number>,
): number[] {
  const out: number[] = [];
  unlockedShapes(records).forEach((on, i) => {
    if (on && !isClaimed(claimed, i)) out.push(i);
  });
  return out;
}

/**
 * 次に遊ぶ枠（**クリア画面の NEXT**・2026-08-06 追加）。
 * 今の枠の**次から順に**見て、最初に見つかった「**解放済みかつ未クリア**」の枠を返す。
 *
 * 巡回は添字の順＝**同じ形状の Easy → Normal → Hard を片付けてから次の形状へ**。
 * **遊べない形状は飛ばす**（ロック中はもちろん、**資格はあるが未解錠**のものも＝
 * UnLock を押していない形状へ連れて行くと鍵マークの盤面が始まってしまう）。
 * タイトルの ◀▶ が `stepUnlockedShape` でロックを飛ばすのと同じ方針。
 *
 * ⚠️ **全部クリア済みでも `null` にしない**。その場合は「次の遊べる枠」を返す＝
 * NEXT が押せるのに何も起きない、という行き止まりを作らないため。
 * `null` になるのは遊べる形状が1つも無いときだけで、先頭3形状は無条件なので通常は起こらない。
 */
export function nextUncleared(
  records: readonly StageRecord[],
  current: number,
  claimed: ReadonlySet<number>,
): { shapeIndex: number; level: BombLevel } | null {
  const unlocked = playableShapes(records, claimed);
  let fallback: number | null = null;
  // step は 1..SLOT_COUNT ＝ 一周して `current` 自身にも戻る（今クリアした枠は
  // `clears > 0` なので候補にはならず、全クリア時の fallback にだけなる）。
  for (let step = 1; step <= SLOT_COUNT; step++) {
    const slot = (((current + step) % SLOT_COUNT) + SLOT_COUNT) % SLOT_COUNT;
    if (!unlocked[fromSlot(slot).shapeIndex]) continue;
    if (records[slot].clears === 0) return fromSlot(slot);
    if (fallback === null) fallback = slot;
  }
  return fallback === null ? null : fromSlot(fallback);
}

// ---------------------------------------------------------------- 集計
/** 通算の★合計（ランキングの「★スター合計」と同じ数え方）。 */
export function totalStars(records: readonly StageRecord[]): number {
  return records.reduce((a, r) => a + r.stars, 0);
}

/** 通算クリア数 / プレイ数 / プレイ時間。 */
export function totals(records: readonly StageRecord[]): {
  clears: number;
  games: number;
  playMs: number;
} {
  return records.reduce(
    (a, r) => ({
      clears: a.clears + r.clears,
      games: a.games + r.games,
      playMs: a.playMs + r.playMs,
    }),
    { clears: 0, games: 0, playMs: 0 },
  );
}

/** ★の理論上限（45枠 × 3）。STATISTICS の「◯ / 135」の分母。 */
export const MAX_TOTAL_STARS = SLOT_COUNT * MAX_RECORD_STARS;

/** 難易度1つぶんの集計。 */
export interface LevelSummary {
  games: number;
  clears: number;
  stars: number;
  /** その難易度で1回以上クリアした形状の数（0..15）。 */
  clearedShapes: number;
}

/** STATISTICS 画面に出す集計一式。**画面はこれを読んで並べるだけ**にする（移植方針1）。 */
export interface ProgressSummary {
  games: number;
  clears: number;
  playMs: number;
  stars: number;
  unlockedShapes: number;
  /** 自己ベスト（未クリアなら null）。同点なら先に見つかった枠。 */
  best: { score: number; shapeIndex: number; level: BombLevel } | null;
  /** 最速クリア（未クリアなら null）。 */
  fastest: { ms: number; shapeIndex: number; level: BombLevel } | null;
  byLevel: LevelSummary[];
}

/**
 * 45枠を1回走査して集計する。
 * **未クリア枠は best / fastest の候補にしない**（`fastestMs === 0` が「未クリア」の意味なので、
 * 素直に最小値を取ると 0 が最速になってしまう）。
 */
export function summarize(records: readonly StageRecord[]): ProgressSummary {
  const byLevel: LevelSummary[] = Array.from({ length: BOMB_LEVEL_COUNT }, () => ({
    games: 0,
    clears: 0,
    stars: 0,
    clearedShapes: 0,
  }));
  let games = 0;
  let clears = 0;
  let playMs = 0;
  let stars = 0;
  let best: ProgressSummary['best'] = null;
  let fastest: ProgressSummary['fastest'] = null;

  for (let slot = 0; slot < SLOT_COUNT; slot++) {
    const r = records[slot];
    const { shapeIndex, level } = fromSlot(slot);
    games += r.games;
    clears += r.clears;
    playMs += r.playMs;
    stars += r.stars;

    const lv = byLevel[level];
    lv.games += r.games;
    lv.clears += r.clears;
    lv.stars += r.stars;
    if (r.clears > 0) lv.clearedShapes++;

    if (r.clears > 0) {
      if (!best || r.best > best.score) best = { score: r.best, shapeIndex, level };
      if (r.fastestMs > 0 && (!fastest || r.fastestMs < fastest.ms)) {
        fastest = { ms: r.fastestMs, shapeIndex, level };
      }
    }
  }

  return { games, clears, playMs, stars, unlockedShapes: unlockedCount(records), best, fastest, byLevel };
}

/**
 * その形状を全難易度クリア済みか（形状選択のバッジ用）。
 * ⚠️ **★では判定しない**。★0 でのクリア（基準時間の2倍超）がありうるので、
 * 「★が入っているか」と「クリアしたか」は別物。
 */
export function isShapeFullyCleared(records: readonly StageRecord[], shapeIndex: number): boolean {
  for (let level = 0; level < BOMB_LEVEL_COUNT; level++) {
    if (records[slotIndex(shapeIndex, level as BombLevel)].clears === 0) return false;
  }
  return true;
}

/**
 * その形状を**一度でもクリアしたことがあるか**（どれか1つの難易度で `clears > 0`）。
 * 解錠記録の後始末（[net/unlock.ts](../net/unlock.ts) の `repairUnplayedClaims`）が使う＝
 * 「実際に遊べていた形状」と「引き継ぎで解錠済みにされただけの形状」を分ける唯一の手がかり。
 * ⚠️ `games`（プレイ回数）ではなく `clears` で見る。遊んで爆死しただけの形状まで
 *    「解錠を使った」と見なすと、UnLock の演出を取り戻せなくなる。
 */
export function isShapePlayed(records: readonly StageRecord[], shapeIndex: number): boolean {
  for (let level = 0; level < BOMB_LEVEL_COUNT; level++) {
    if (records[slotIndex(shapeIndex, level as BombLevel)].clears > 0) return true;
  }
  return false;
}

/**
 * 盤面のシード。**同じ形状・同じ難易度なら常に同じ配置**にする
 * （二角取りの `stageSeed(stageNo, partIndex)` = 原作 `stageCurNo*100 + stagePartCurNo` と同じ考え方）。
 * 原作も `setupBombs:randseed:` にシードを渡す形になっていた。
 */
export function boardSeed(shapeIndex: number, level: BombLevel): number {
  return (shapeIndex + 1) * 100 + level;
}
